// Advanced cache utility with localStorage persistence and stale-while-revalidate
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.defaultTTL = 10 * 60 * 1000; // 10 minutes default
    this.staleTime = 2 * 60 * 1000; // 2 minutes - serve stale data while revalidating
    this.storageKey = 'crm_api_cache';
    this.saveTimeout = null; // For debouncing saves
    this.loadFromStorage();

    // Save cache when page unloads
    window.addEventListener('beforeunload', () => {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveToStorageImmediate();
    });
  }

  // Load cache from localStorage on init
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const { cache, timestamps } = JSON.parse(stored);

        // Only restore non-expired entries
        const now = Date.now();
        Object.entries(cache || {}).forEach(([key, value]) => {
          const expiry = timestamps[key];
          if (expiry && expiry > now) {
            this.cache.set(key, value);
            this.timestamps.set(key, expiry);
          }
        });
      }
    } catch (error) {
      console.warn('Failed to load cache from storage:', error);
    }
  }

  // Save cache to localStorage (debounced)
  saveToStorage() {
    // Debounce saves to reduce localStorage writes
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(() => {
      this.saveToStorageImmediate();
    }, 1000); // Wait 1 second before saving
  }

  // Immediate save (used on page unload)
  saveToStorageImmediate() {
    try {
      const cache = {};
      const timestamps = {};

      this.cache.forEach((value, key) => {
        cache[key] = value;
        timestamps[key] = this.timestamps.get(key);
      });

      localStorage.setItem(this.storageKey, JSON.stringify({ cache, timestamps }));
    } catch (error) {
      console.warn('Failed to save cache to storage:', error);
    }
  }

  set(key, value, ttl = this.defaultTTL) {
    this.cache.set(key, value);
    this.timestamps.set(key, Date.now() + ttl);
    this.saveToStorage();
  }

  get(key) {
    return this.cache.get(key) || null;
  }

  // Check if cache is fresh (within staleTime)
  isFresh(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return false;
    return Date.now() < timestamp - this.staleTime;
  }

  // Check if cache is stale but still valid (for stale-while-revalidate)
  isStale(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return false;
    const now = Date.now();
    return now >= timestamp - this.staleTime && now < timestamp;
  }

  // Check if cache is expired
  isExpired(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return true;
    return Date.now() >= timestamp;
  }

  has(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp || Date.now() > timestamp) {
      this.cache.delete(key);
      this.timestamps.delete(key);
      this.saveToStorage();
      return false;
    }
    return true;
  }

  clear() {
    this.cache.clear();
    this.timestamps.clear();
    localStorage.removeItem(this.storageKey);
  }

  invalidate(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
    this.saveToStorage();
  }

  invalidatePattern(pattern) {
    const regex = new RegExp(pattern);
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
        this.cache.delete(key);
        this.timestamps.delete(key);
      }
    }
    this.saveToStorage();

    // Debug logging
    if (keysToDelete.length > 0 && this.debugMode) {
      console.log(`[Cache] Invalidated ${keysToDelete.length} keys matching pattern: ${pattern}`, keysToDelete);
    }
  }

  // Debug utilities
  enableDebug() {
    this.debugMode = true;
    console.log('[Cache] Debug mode enabled');
  }

  disableDebug() {
    this.debugMode = false;
  }

  getStats() {
    const now = Date.now();
    const stats = {
      totalKeys: this.cache.size,
      fresh: 0,
      stale: 0,
      expired: 0,
      keys: []
    };

    for (const [key, value] of this.cache.entries()) {
      const timestamp = this.timestamps.get(key);
      const age = timestamp ? now - (timestamp - this.defaultTTL) : 0;
      const ttl = timestamp ? timestamp - now : 0;

      let status = 'expired';
      if (this.isFresh(key)) {
        status = 'fresh';
        stats.fresh++;
      } else if (this.isStale(key)) {
        status = 'stale';
        stats.stale++;
      } else {
        stats.expired++;
      }

      stats.keys.push({
        key,
        status,
        age: Math.round(age / 1000) + 's',
        ttl: Math.round(ttl / 1000) + 's',
        size: JSON.stringify(value).length
      });
    }

    return stats;
  }

  printStats() {
    const stats = this.getStats();
    console.group('[Cache Stats]');
    console.log(`Total Keys: ${stats.totalKeys}`);
    console.log(`Fresh: ${stats.fresh} | Stale: ${stats.stale} | Expired: ${stats.expired}`);
    console.table(stats.keys);
    console.groupEnd();
  }
}

export const apiCache = new CacheManager();

// Expose cache utilities to window for debugging in console
if (typeof window !== 'undefined') {
  window.__cacheDebug = {
    enable: () => apiCache.enableDebug(),
    disable: () => apiCache.disableDebug(),
    stats: () => apiCache.getStats(),
    print: () => apiCache.printStats(),
    clear: () => {
      apiCache.clear();
      console.log('[Cache] All cache cleared');
    },
    invalidate: (pattern) => {
      apiCache.invalidatePattern(pattern);
      console.log(`[Cache] Invalidated pattern: ${pattern}`);
    }
  };

  console.log('%c[Cache Debug]%c Available in console as window.__cacheDebug',
    'background: #55C5D0; color: white; padding: 2px 6px; border-radius: 3px;',
    'color: #666;'
  );
  console.log('Commands: enable(), disable(), stats(), print(), clear(), invalidate(pattern)');
}

// Cache key generators
export const cacheKeys = {
  leads: (filters = {}) => `leads-${JSON.stringify(filters)}`,
  properties: (filters = {}) => `properties-${JSON.stringify(filters)}`,
  publicProperties: (filters = {}) => `public-properties-${JSON.stringify(filters)}`,
  tasks: (filters = {}) => `tasks-${JSON.stringify(filters)}`,
  contacts: (filters = {}) => `contacts-${JSON.stringify(filters)}`,
  agents: (filters = {}) => `agents-${JSON.stringify(filters)}`,
  buyers: (filters = {}) => `buyers-${JSON.stringify(filters)}`,
  commissions: () => 'commissions',
  kpi: (month) => `kpi-${month}`,
  dashboard: () => 'dashboard-stats',
  inspections: (filters = {}) => `inspections-${JSON.stringify(filters)}`,
  inspectionAnalytics: () => 'inspections-analytics',
  inspectionAttendees: (id) => `inspection-attendees-${id}`,
  // Settings cache keys
  settingsAdminNotifications: () => 'settings-admin-notifications',
  settingsAgentNotifications: () => 'settings-agent-notifications',
  settingsAIPersona: () => 'settings-ai-persona',
  settingsChatbot: () => 'settings-chatbot',
  settingsTwilio: () => 'settings-twilio',
  settingsVoiceAssistants: () => 'settings-voice-assistants',
  settingsAll: () => 'settings-',
};

// Enhanced wrapper for cached API calls with stale-while-revalidate
export const cachedApiCall = async (cacheKey, apiFunction, ttl, options = {}) => {
  const {
    forceRefresh = false,
    onStaleData = null
  } = options;

  // Debug logging
  if (apiCache.debugMode) {
    console.log(`[Cache] Request for key: ${cacheKey}`, { forceRefresh });
  }

  // If force refresh, skip cache
  if (forceRefresh) {
    if (apiCache.debugMode) {
      console.log(`[Cache] Force refresh - fetching fresh data`);
    }
    const response = await apiFunction();
    apiCache.set(cacheKey, response, ttl);
    return response;
  }

  const cached = apiCache.get(cacheKey);

  // If cache is fresh, return it immediately
  if (cached && apiCache.isFresh(cacheKey)) {
    if (apiCache.debugMode) {
      console.log(`[Cache] HIT (fresh) - returning cached data`);
    }
    return cached;
  }

  // If cache is stale but valid, return it AND fetch fresh data in background
  if (cached && apiCache.isStale(cacheKey)) {
    if (apiCache.debugMode) {
      console.log(`[Cache] HIT (stale) - returning cached data and refreshing in background`);
    }

    // Return stale data immediately
    if (onStaleData) {
      onStaleData(cached);
    }

    // Fetch fresh data in background (don't await)
    apiFunction()
      .then((response) => {
        apiCache.set(cacheKey, response, ttl);
        if (apiCache.debugMode) {
          console.log(`[Cache] Background refresh completed for: ${cacheKey}`);
        }
      })
      .catch((error) => {
        console.warn('[Cache] Background refresh failed:', error);
      });

    return cached;
  }

  // No cache or expired - fetch fresh data
  if (apiCache.debugMode) {
    console.log(`[Cache] MISS - fetching fresh data`);
  }
  const response = await apiFunction();
  apiCache.set(cacheKey, response, ttl);

  return response;
};

// Helper function to invalidate specific cache patterns
// Broadcast so anything outside the calling page can react to a change it did
// not make - the sidebar badges listen for these instead of waiting out their
// 60s poll, so ignoring or assigning a lead drops the number immediately rather
// than leaving a count sitting over a page the agent has just cleared
// (Christian Gravias, Monday 12944444309).
const announce = (name) => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(`cache-invalidated:${name}`));
  }
};

export const invalidateCache = {
  leads: () => {
    apiCache.invalidatePattern('leads-');
    apiCache.invalidate('dashboard_buyer_enquiries');
    announce('leads');
  },
  properties: () => {
    // Blow away every cached property listing so edits (e.g. address_publish_mode)
    // surface immediately on Properties, Collings Properties, and Public pages.
    apiCache.invalidatePattern('properties-');
    apiCache.invalidatePattern('collings-properties-');
    apiCache.invalidatePattern('collings-crm-');
    apiCache.invalidatePattern('collings-rea-');
    apiCache.invalidatePattern('public-properties-');
    announce('properties');
  },
  collingsProperties: () => {
    apiCache.invalidatePattern('collings-properties-');
    apiCache.invalidatePattern('collings-crm-');
    apiCache.invalidatePattern('collings-rea-');
  },
  publicProperties: () => apiCache.invalidatePattern('public-properties-'),
  tasks: () => apiCache.invalidatePattern('tasks-'),
  contacts: () => apiCache.invalidatePattern('contacts-'),
  agents: () => apiCache.invalidatePattern('agents-'),
  buyers: () => apiCache.invalidatePattern('buyers-'),
  kpi: () => apiCache.invalidatePattern('kpi-'),
  commissions: () => apiCache.invalidate('commissions'),
  dashboard: () => apiCache.invalidate('dashboard-stats'),
  inspections: () => {
    apiCache.invalidatePattern('inspections-');
    apiCache.invalidate('inspections-analytics');
    apiCache.invalidatePattern('inspection-attendees-');
  },
  // Settings cache invalidation
  settings: () => apiCache.invalidatePattern('settings-'),
  settingsAdminNotifications: () => apiCache.invalidate('settings-admin-notifications'),
  settingsAgentNotifications: () => apiCache.invalidate('settings-agent-notifications'),
  settingsAIPersona: () => apiCache.invalidate('settings-ai-persona'),
  settingsChatbot: () => apiCache.invalidate('settings-chatbot'),
  settingsTwilio: () => apiCache.invalidate('settings-twilio'),
  settingsVoiceAssistants: () => apiCache.invalidate('settings-voice-assistants'),
  all: () => apiCache.clear(),
};

